// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from "react"
import { formatInTimeZone } from "date-fns-tz"
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Download
} from "lucide-react"
import type { AnalysisSummary } from "@/api/analysis-history"

interface AnalysisHistoryTableProps {
  analyses: AnalysisSummary[]
  onViewDetails: (analysisId: string) => void
  onDelete: (analysisId: string) => void
  onDownload: (analysisId: string) => void
  onDownloadPDF: (analysisId: string) => void
  isLoading?: boolean
  loadingAnalysisId?: string | null
  loadingAction?: 'view' | 'pdf' | 'download' | null
}

export function AnalysisHistoryTable({
  analyses,
  onViewDetails,
  onDelete,
  onDownload,
  onDownloadPDF,
  isLoading = false,
  loadingAnalysisId = null,
  loadingAction = null
}: AnalysisHistoryTableProps) {
  const [sortBy, setSortBy] = useState<'created_at' | 'status' | 'llm_cost' | 'document_count'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Sort analyses
  const sortedAnalyses = [...analyses].sort((a, b) => {
    let aVal: any = a[sortBy]
    let bVal: any = b[sortBy]

    // Handle date sorting
    if (sortBy === 'created_at') {
      aVal = a.created_at ? new Date(a.created_at).getTime() : 0
      bVal = b.created_at ? new Date(b.created_at).getTime() : 0
    }

    // Handle cost sorting
    if (sortBy === 'llm_cost') {
      aVal = parseFloat(a.llm_cost || '0')
      bVal = parseFloat(b.llm_cost || '0')
    }

    // Handle document count sorting
    if (sortBy === 'document_count') {
      aVal = a.document_count || 0
      bVal = b.document_count || 0
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1
    } else {
      return aVal < bVal ? 1 : -1
    }
  })

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'processing':
        return <Clock className="h-5 w-5 text-yellow-500 animate-spin" />
      default:
        return <Clock className="h-5 w-5 text-gray-400" />
    }
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      case 'processing':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Pagination logic
  const totalAnalyses = sortedAnalyses.length
  const totalPages = Math.ceil(totalAnalyses / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedAnalyses = sortedAnalyses.slice(startIndex, endIndex)

  // Handle column sort
  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
      setCurrentPage(1)
    }
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setCurrentPage(1)
  }

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }

  if (isLoading && !loadingAnalysisId) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  if (analyses.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No analyses found</p>
        <p className="text-sm text-gray-500 mt-2">Create a new analysis to get started</p>
      </div>
    )
  }

  return (
    <>
    {/* Pagination Controls - Top */}
    <div className="flex justify-between items-center mb-4">
      <div className="text-sm text-gray-600">
        Showing {startIndex + 1}-{Math.min(endIndex, totalAnalyses)} of {totalAnalyses} analyses
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Rows per page:</label>
        <select
          value={pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>
    <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('created_at')}
            >
              <div className="flex items-center gap-1">
                Created
                {sortBy === 'created_at' && (
                  sortOrder === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('document_count')}
            >
              <div className="flex items-center gap-1">
                Documents
                {sortBy === 'document_count' && (
                  sortOrder === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('status')}
            >
              <div className="flex items-center gap-1">
                Status
                {sortBy === 'status' && (
                  sortOrder === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                )}
              </div>
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              Actions
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Prompt
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created By
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('llm_cost')}
            >
              <div className="flex items-center gap-1">
                Cost
                {sortBy === 'llm_cost' && (
                  sortOrder === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {paginatedAnalyses.map((analysis) => (
            <tr
              key={analysis.analysis_id}
              className="hover:bg-gray-50 transition-colors"
            >
              {/* Created Date */}
              <td className="px-3 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {analysis.created_at ? (() => {
                    let dateStr = analysis.created_at;
                    // Ensure proper ISO format for timezone conversion
                    if (!dateStr.includes('+') && !dateStr.endsWith('Z')) {
                      dateStr = dateStr + 'Z';
                    }
                    return formatInTimeZone(new Date(dateStr), 'America/New_York', 'MMM d, yyyy h:mm a');
                  })() : '-'}
                </div>
              </td>

              {/* Document Count */}
              <td className="px-4 py-4">
                {analysis.filenames && analysis.filenames.length > 0 ? (
                  <div>
                    <ul className="text-xs text-gray-600 space-y-0.5">
                      {analysis.filenames.map((filename, idx) => (
                        <li key={idx} className="truncate max-w-xs" title={filename}>
                           {filename}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-sm text-gray-900 font-medium">
                    {analysis.document_count} {analysis.document_count === 1 ? 'document' : 'documents'}
                  </div>
                )}
              </td>

              {/* Status */}
              <td className="px-3 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  {getStatusIcon(analysis.status)}
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(analysis.status)}`}>
                    {analysis.status}
                  </span>
                </div>
                {analysis.completed_at && (
                  <div className="text-xs text-gray-500 mt-1">
                    {(() => {
                      let dateStr = analysis.completed_at;
                      // Ensure proper ISO format for timezone conversion
                      if (!dateStr.includes('+') && !dateStr.endsWith('Z')) {
                        dateStr = dateStr + 'Z';
                      }
                      return formatInTimeZone(new Date(dateStr), 'America/New_York', 'MMM d, yyyy h:mm a');
                    })()}
                  </div>
                )}
              </td>

              {/* Actions */}
              <td className="px-3 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  {analysis.status === 'completed' && (
                    <>
                      <button
                        onClick={() => onViewDetails(analysis.analysis_id)}
                        disabled={loadingAnalysisId === analysis.analysis_id && loadingAction === 'view'}
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                        title="View analysis"
                        aria-label="View analysis details"
                      >
                        {loadingAnalysisId === analysis.analysis_id && loadingAction === 'view' ? (
                          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => onDownloadPDF(analysis.analysis_id)}
                        disabled={loadingAnalysisId === analysis.analysis_id && loadingAction === 'pdf'}
                        className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                        title="Download accessible PDF"
                        aria-label="Download accessible PDF analysis report"
                      >
                        {loadingAnalysisId === analysis.analysis_id && loadingAction === 'pdf' ? (
                          <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => onDelete(analysis.analysis_id)}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                    title="Delete analysis"
                    aria-label="Delete analysis"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>

              {/* Prompt */}
              <td className="px-4 py-4">
                <div className="text-sm text-gray-900">
                  {analysis.prompt_name || '-'}
                </div>
                {analysis.analysis_type && (
                  <div className="text-xs text-gray-500">
                    {analysis.analysis_type}
                  </div>
                )}
                {analysis.prompt_type && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                    {analysis.prompt_type}
                  </span>
                )}
              </td>

              {/* Created By */}
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {analysis.created_by || '-'}
                </div>
              </td>

              {/* Cost */}
              <td className="px-3 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {analysis.llm_cost ? `$${parseFloat(analysis.llm_cost).toFixed(4)}` : '-'}
                </div>
                {analysis.result_size_mb && (
                  <div className="text-xs text-gray-500 mt-1">
                    {parseFloat(analysis.result_size_mb).toFixed(2)} MB
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Pagination Controls - Bottom */}
    {totalPages > 1 && (
      <div className="flex justify-between items-center pt-4 border-t mt-4">
        <div className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
          >
            ← Previous
          </button>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
          >
            Next →
          </button>
        </div>
      </div>
    )}
    </>
  )
}
